import { GroupType } from '../enumerations';

/**
 * @ngdoc type
 * @name mx.SolutionBuilderAgent.SearchQueryGroup
 * @module mx.shell
 *
 * @description
 * An object containing search query information (Search query model)
 * Constructor accepts groupOptions as param like:
 * ```javascript
 var groupOptions = {
	LogicalOperator: mx.SolutionBuilderAgent.SearchQueryLogicalOperator.OR
 };

 var group = new mx.SolutionBuilderAgent.SearchQueryGroup(groupOptions);
 * ```
 * @property {SearchQueryGroup[]} Groups - Gets or sets array of nested query group models. Default: []
 * @property {SearchQueryCondition[]} Conditions - Gets or sets group nested {@ref mx.SolutionBuilderAgent.SearchQueryCondition condition models}. Default: []
 * @property {SearchQueryLogicalOperator | int} LogicalOperator - Gets or sets group logical operator. Default: mx.SolutionBuilderAgent.SearchQueryLogicalOperator.AND (1)
 * @property {string} LogicalOperatorTitle - Gets group logical operator title
 */
angular.module('mx.SolutionBuilderAgent').factory('mx.SolutionBuilderAgent.SearchQueryGroup', searchQueryGroupFactory);
searchQueryGroupFactory.$inject = ['mx.sb.Agent.DataQuery.FilterOperatorsFactory', 'mx.utils', 'mx.SolutionBuilderAgent.SearchQueryCondition'];
export default function searchQueryGroupFactory(filterOperatorsFactory, utils, SearchQueryCondition) {
	class SearchQueryGroup {
		constructor(groupOptions) {
			groupOptions = groupOptions || {};

			this.type = GroupType.Basic;
			const groups = groupOptions.Groups ? groupOptions.Groups.map((g) => new SearchQueryGroup(g)) : [];
			const conditions = groupOptions.Conditions ? groupOptions.Conditions.map((c) => new SearchQueryCondition(c)) : [];

			Object.defineProperty(this, 'id', {
				writable: true,
				enumerable: true,
				value: groupOptions.Id || utils.guid(),
			});

			Object.defineProperty(this, 'groups', {
				writable: true,
				enumerable: true,
				value: groups,
			});

			Object.defineProperty(this, 'conditions', {
				writable: true,
				enumerable: true,
				value: conditions,
			});

			Object.defineProperty(this, 'logicalOperator', {
				writable: true,
				enumerable: true,
				value: Number(groupOptions.LogicalOperator || mx.SolutionBuilderAgent.SearchQueryLogicalOperator.AND),
			});

			// ToDo localize titles
			Object.defineProperty(this, 'logicalOperatorTitle', {
				enumerable: true,
				get: function () {
					if (this.logicalOperator === mx.SolutionBuilderAgent.SearchQueryLogicalOperator.AND) {
						return 'AND';
					}
					if (this.logicalOperator === mx.SolutionBuilderAgent.SearchQueryLogicalOperator.OR) {
						return 'OR';
					}
					return '';
				},
			});
		}

		/**
		 * @ngdoc method
		 * @name SearchQueryGroup#addGroup
		 *
		 * @description
		 * Adds new group as a child group of a current group.<br>
		 * If group already exists no actions performed.
		 *
		 * @param {SearchQueryGroup} group - Child group model
		 * @param {GroupType=Basic} type - Group type
		 * @return {boolean} True if child group was added.<br>False if child group was not added as it already existed as a child
		 */
		addGroup(group, type = GroupType.Basic) {
			if (!group) {
				throw new Error('SearchQueryGroup: addGroup group is not provided');
			}

			if (!(group instanceof SearchQueryGroup)) {
				throw new Error('SearchQueryGroup: parameter group has invalid type');
			}

			if (typeof type !== 'string') {
				throw new Error('Wrong group type');
			}

			if (this.groups.indexOf(group) !== -1) {
				return false;
			}

			if (type !== GroupType.Basic) {
				if (this.getGroup(type)) {
					throw new Error(`Group with type=${type} already exists`);
				}
			}
			group.type = type;
			this.groups.push(group);
			return true;
		}

		getGroup(type) {
			return type ? this.groups.find((group) => group.type === type) : null;
		}

		setGroup(group, type) {
			const previousGroup = this.getGroup(type);
			if (previousGroup) {
				this.removeGroup(previousGroup);
			}

			this.addGroup(group, type);
			return group;
		}

		/**
		 * @ngdoc method
		 * @name SearchQueryGroup#removeGroup
		 *
		 * @description
		 * Removes group from current group's children.<br>
		 * If specified child group does not exist - no actions performed.
		 *
		 * @param {SearchQueryGroup} group - Child group model
		 * @return {boolean} True if child group was removed.<br>False if child group was not removed as it was not found among child groups
		 */
		removeGroup(group) {
			if (!group) {
				throw new Error('SearchQueryGroup: removeGroup group is not provided');
			}
			const index = this.groups.indexOf(group);

			if (index === -1) {
				return false;
			}
			this.groups.splice(index, 1);
			return true;
		}

		/**
		 * @ngdoc method
		 * @name SearchQueryGroup#addCondition
		 *
		 * @description
		 * Adds new condition to current group.<br>
		 * If condition exists within current group no actions performed.
		 *
		 * @param {SearchQueryCondition} condition - {@ref mx.SolutionBuilderAgent.SearchQueryCondition Search query condition model}
		 * @return {boolean} True if condition was added.<br>False if condition was not added as it already exists in group
		 */
		addCondition(condition) {
			if (!condition) {
				throw new Error('SearchQueryGroup: addCondition condition is not provided');
			}
			if (this.conditions.indexOf(condition) !== -1) {
				return false;
			}
			this.conditions.push(condition);
			return true;
		}

		/**
		 * @ngdoc method
		 * @name SearchQueryGroup#removeCondition
		 *
		 * @description
		 * Removes condition from current group.<br>
		 * If condition does not exist within current group no actions performed.
		 *
		 * @param {guid} id - Search query condition id
		 * @return {boolean} True if condition was removed.<br>False if condition was not removed as it was not in group
		 */
		removeCondition(id) {
			if (!id) {
				throw new Error('SearchQueryGroup: removeCondition - id is not provided');
			}
			_.remove(this.conditions, (c) => c.id === id);
		}

		/**
		 * @ngdoc method
		 * @name SearchQueryGroup#toSqlView
		 *
		 * @description
		 * Converts own conditions and recursive nested groups conditions to pseudo-SQL string
		 *
		 * @param {int} shift - Indentation of a group block (used for building nested groups queries)
		 * @return {string} Group pseudo-SQL presentation
		 */
		toSqlView(shift) {
			const tab = '    ';
			const lineBreak = '\r\n';

			shift = shift || 1;

			const tabSpace = tab.repeat(shift);

			let str = `${tabSpace}(${lineBreak}`;

			let i = 0;
			this.conditions.forEach((condition) => {
				if (i > 0) {
					str += tabSpace + tab + tab + this.logicalOperatorTitle + ' ' + lineBreak;
				}

				str += tabSpace + tab + condition.toSqlView() + '"' + lineBreak;
				i++;
			});

			this.groups.forEach((group) => {
				str += tabSpace + tab + tab + this.LogicalOperatorTitle + ' ' + lineBreak;
				str += group.toSqlView(shift + 1) + lineBreak;
			});

			str += `${tabSpace})`;

			return str;
		}

		/**
		 * @ngdoc method
		 * @name SearchQueryCondition#toJSON
		 *
		 * @description
		 * Returns JSON serialised search query condition prepared for server side
		 *
		 * @return {object} return object which represents search query condition
		 */
		toJSON() {
			return {
				Groups: this.groups.map((g) => g.toJSON()),
				Conditions: this.conditions.map((c) => c.toJSON()),
				LogicalOperator: this.logicalOperator,
			};
		}

		/**
		 * @ngdoc method
		 * @name SearchQueryCondition#clone
		 *
		 * @description
		 * Makes clone of the group
		 *
		 * @return {SearchQueryGroup}
		 */
		clone() {
			const cloneGroup = new SearchQueryGroup();

			cloneGroup.logicalOperator = this.logicalOperator;
			cloneGroup.groups = this.groups.slice();
			cloneGroup.conditions = this.conditions.slice();

			return cloneGroup;
		}
	}

	return SearchQueryGroup;
}
